import { useState } from "react"
import axiosInstance from "../../../services/axios-client"
import { toast } from "react-toastify"
import { useNavigate } from "react-router-dom"

const useRatingQuota = () => {
    const [projectID, setProjectID] = useState()
    const [isSaving, setIsSaving] = useState(false)
    const [payload, setPayload] = useState({
        ProjectID: projectID,
        APlusQuota: 0,
        AQuota: 0,
        BPlusQuota: 0,
        BQuota: 0,
        CQuota: 0,
        DQuota: 0,
        Remaining: "B+",
        Excess: "B"
    })
    const [businessUnits, setBusinessUnits] = useState()
    const [ratingChoices, setRatingChoices] = useState([
        { label: 'A+', value: 'A+' },
        { label: 'A', value: 'A' },
        { label: 'B+', value: 'B+' },
        { label: 'B', value: 'B' },
        { label: 'C', value: 'C' },
        { label: 'D', value: 'D' },
    ])
    const navigate = useNavigate()

    const onChangeHandler = (e) => {
        let value = e.target.value === '' ? '' : e.target.value

        // Validate that number inputs don't exceed 100
        if (e.target.type === 'number' && value !== '') {
            const numValue = parseFloat(value)
            if (numValue > 100) {
                toast.error("Quota value cannot exceed 100%")
                return
            }
            if (numValue < 0) {
                toast.error("Quota value cannot be negative")
                return
            }
        }

        setPayload(
            { ...payload, [e.target.name]: value }
        )
    }

    const handleSelectBusinessUnit = (value) => {
        setPayload({
            ...payload,
            BusinessUnitID: value
        })
    }

    const handleSelectRemaining = (value) => {
        setPayload({
            ...payload,
            Remaining: value
        })
    }

    const handleSelectExcess = (value) => {
        setPayload({
            ...payload,
            Excess: value
        })
    }

    const getBusinessUnits = async () => {
        try {
            let res = await axiosInstance.get('/auth/business-units/all')
            if (res.status === 200) {
                let BUs = []
                for (const bu of res.data.data) {
                    BUs.push({
                        'label': bu.Name,
                        'value': bu.ID
                    })
                }
                BUs.sort((a, b) => a.label.localeCompare(b.label))
                setBusinessUnits(BUs)
            }
        } catch (error) {
            if (error.response) {
                toast.error(error?.response?.data?.status?.description)
            } else if (error.message) {
                toast.error(error.message)
            } else {
                toast.error(error)
            }
        }
    }

    const getRatingQuota = async (projectID, businessUnitID) => {
        try {
            let res = await axiosInstance.get(`/auth/rating-quotas/${projectID}/${businessUnitID}`)
            if (res.status === 200) {
                setPayload(res.data.data)
            }
        } catch (error) {
            if (error.response) {
                toast.error(error?.response?.data?.status?.description)
            } else if (error.message) {
                toast.error(error.message)
            } else {
                toast.error(error)
            }
        }
    }

    const putRatingQuotaForm = async (e) => {
        e.preventDefault()
        setIsSaving(true)
        try {
            if (!checkingTotalRatingQuotaExceeds(true)) {
                const data = {
                    ...payload,
                    APlusQuota: parseFloat(payload.APlusQuota),
                    AQuota: parseFloat(payload.AQuota),
                    BPlusQuota: parseFloat(payload.BPlusQuota),
                    BQuota: parseFloat(payload.BQuota),
                    CQuota: parseFloat(payload.CQuota),
                    DQuota: parseFloat(payload.DQuota)
                }
                let res = await axiosInstance.put('/auth/rating-quotas', data)
                if (res.status === 200) {
                    navigate(-1)
                    toast.success(`Success Update Rating Quota`)
                }
            }
        } catch (error) {
            if (error.response) {
                toast.error(error?.response?.data?.status?.description)
            } else if (error.message) {
                toast.error(error.message)
            } else {
                toast.error(error)
            }
        } finally {
            setIsSaving(false)
        }
    }

    const postRatingQuotaForm = async (e) => {
        e.preventDefault()
        setIsSaving(true)
        try {
            let data = {
                ...payload,
                ProjectID: projectID,
                APlusQuota: parseFloat(payload.APlusQuota),
                AQuota: parseFloat(payload.AQuota),
                BPlusQuota: parseFloat(payload.BPlusQuota),
                BQuota: parseFloat(payload.BQuota),
                CQuota: parseFloat(payload.CQuota),
                DQuota: parseFloat(payload.DQuota)
            }
            if (!checkingTotalRatingQuotaExceeds(true)) {
                let findRQ = await axiosInstance.get(`/auth/rating-quotas/${data.ProjectID}/${data.BusinessUnitID}`)
                const uuidNil = '00000000-0000-0000-0000-000000000000'
                if (findRQ.data.data.ProjectID && findRQ.data.data.ProjectID !== uuidNil) {
                    toast.error("You already input Rating Quota for this Business Unit")
                } else {
                    let res = await axiosInstance.post('/auth/rating-quotas', data)
                    if (res.status === 200) {
                        navigate(-1)
                        toast.success(`Success Create Rating Quota`)
                    }
                }
            }
        } catch (error) {
            if (error.response) {
                toast.error(error?.response?.data?.status?.description)
            } else if (error.message) {
                toast.error(error.message)
            } else {
                toast.error(error)
            }
        } finally {
            setIsSaving(false)
        }
    }

    const checkingTotalRatingQuotaExceeds = (postPut) => {
        const total = parseFloat(payload.APlusQuota || 0) +
            parseFloat(payload.AQuota || 0) +
            parseFloat(payload.BPlusQuota || 0) +
            parseFloat(payload.BQuota || 0) +
            parseFloat(payload.CQuota || 0) +
            parseFloat(payload.DQuota || 0)

        if (total > 100) {
            toast.error("Total Quota Cannot Exceeds 100%")
            return true
        } else if (postPut && total < 100) {
            toast.error("Total Quota Cannot Less than 100%")
            return true
        }
        return false
    }

    const getTotalQuota = () => {
        return parseFloat(payload.APlusQuota || 0) +
            parseFloat(payload.AQuota || 0) +
            parseFloat(payload.BPlusQuota || 0) +
            parseFloat(payload.BQuota || 0) +
            parseFloat(payload.CQuota || 0) +
            parseFloat(payload.DQuota || 0)
    }

    return {
        payload,
        projectID,
        setProjectID,
        onChangeHandler,
        ratingChoices,
        businessUnits,
        getBusinessUnits,
        getRatingQuota,
        putRatingQuotaForm,
        postRatingQuotaForm,
        checkingTotalRatingQuotaExceeds,
        getTotalQuota,
        handleSelectBusinessUnit,
        handleSelectRemaining,
        isSaving,
        handleSelectExcess
    }
}

export default useRatingQuota